import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Nav } from '@/marketing/blocks/nav';
import { Footer } from '@/marketing/blocks/footer';
import { Section } from '@/marketing/blocks/Section';
import { posts } from '@/marketing/blog/posts';
import { useCopy } from '@/i18n';

type BlogCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  all: string;
  read_more: string;
};

// Blog index adapted from reactbits-pro blog-6: centered header, tag-filter
// tabs with an animated underline, and an animated card grid.
export default function BlogIndex() {
  const copy = useCopy();
  const c = copy<BlogCopy>('blog');
  const { i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const tags = useMemo(() => [...new Set(posts.flatMap((p) => p.tags))].sort(), []);
  const visible = activeTag ? posts.filter((p) => p.tags.includes(activeTag)) : posts;
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  if (!c) return null;

  return (
    <>
      <Nav ctaPrimary="" />
      <Section id="blog" bg="paper">
        <div className="mx-auto w-full max-w-[1180px]">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.5 }}
            style={{ textAlign: 'center' }}
          >
            <span className="eyebrow">{c.eyebrow}</span>
            <h1 className="h-section" style={{ marginTop: 14 }}>
              {c.title}
            </h1>
            <p className="lead" style={{ maxWidth: '52ch', margin: '18px auto 0' }}>
              {c.lead}
            </p>
          </motion.div>

          {tags.length > 1 ? (
            <nav
              className="mt-12 flex justify-center gap-8"
              style={{ borderBottom: '1px solid var(--line)' }}
            >
              {[null, ...tags].map((tag) => {
                const isActive = activeTag === tag;
                return (
                  <button
                    key={tag ?? 'all'}
                    type="button"
                    onClick={() => setActiveTag(tag)}
                    className="relative pb-3 text-sm transition-colors"
                    style={{
                      color: isActive ? 'var(--ink)' : 'var(--muted)',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {tag ?? c.all}
                    {isActive ? (
                      <motion.span
                        layoutId="blog-tab-underline"
                        className="absolute -bottom-px start-0 end-0 h-0.5"
                        style={{ background: 'var(--coral)' }}
                        transition={
                          reduceMotion
                            ? { duration: 0 }
                            : { type: 'spring', stiffness: 400, damping: 32 }
                        }
                      />
                    ) : null}
                  </button>
                );
              })}
            </nav>
          ) : null}

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout" initial={false}>
              {visible.map((post, i) => (
                <motion.article
                  key={post.slug}
                  layout={!reduceMotion}
                  initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                  transition={{
                    layout: { type: 'spring', stiffness: 260, damping: 30 },
                    opacity: { duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' },
                    y: { duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' },
                    delay: reduceMotion ? 0 : (i % 3) * 0.04,
                  }}
                  className="group flex flex-col overflow-hidden rounded-2xl"
                  style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="flex h-full flex-col p-6"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 10,
                            letterSpacing: '.1em',
                            textTransform: 'uppercase',
                            padding: '3px 8px',
                            borderRadius: 999,
                            background: 'var(--sand)',
                            color: 'var(--ink)',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      <time
                        dateTime={post.date}
                        style={{ fontSize: 12, color: 'var(--muted)', marginInlineStart: 'auto' }}
                      >
                        {dateFmt.format(new Date(post.date))}
                      </time>
                    </div>
                    <h2
                      className="transition-opacity group-hover:opacity-80"
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 24,
                        lineHeight: 1.2,
                        marginTop: 16,
                        color: 'var(--ink)',
                      }}
                    >
                      {post.title}
                    </h2>
                    <p
                      style={{
                        marginTop: 10,
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: 'var(--muted)',
                        flex: 1,
                      }}
                    >
                      {post.description}
                    </p>
                    <span
                      style={{
                        marginTop: 16,
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--coral)',
                      }}
                    >
                      {c.read_more} →
                    </span>
                  </Link>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </Section>
      <Footer />
    </>
  );
}
